import { useEffect, useMemo, useRef, useState } from 'react';

export type SidebarMenuItem = {
  name: string;
  label: string;
  slug: string;
  children?: SidebarMenuItem[];
};

export const SIDEBAR_GROUP_ITEMS: Record<string, SidebarMenuItem[]> = {
  storeConfig: [
    { name: 'generalInfo', label: 'store_settings:general_info', slug: 'general-info' },
    { name: 'storeHours', label: 'store_settings:store_hours', slug: 'store-hours' },
    {
      name: 'equipmentProfile',
      label: 'store_settings:equipment_profile',
      slug: 'equipment-profile',
      children: [
        { name: 'equipmentProfile.config', label: 'store_settings:equipment_profile_page_config', slug: 'store-equipment' },
        { name: 'equipmentProfile.tempEquip', label: 'store_settings:equipment_profile_page_temp_equip', slug: 'temp-equipment' },
        { name: 'equipmentProfile.hardware', label: 'store_settings:equipment_profile_page_hardware', slug: 'hardware' },
        { name: 'equipmentProfile.products', label: 'store_settings:equipment_profile_page_products', slug: 'products' },
      ],
    },
  ],
  teamMgmt: [{ name: 'storeRoster', label: 'store_settings:team_members', slug: 'team-roster' }],
};

const findInitialItem = (items: SidebarMenuItem[], pageModule?: string, pageSubModule?: string): SidebarMenuItem | undefined => {
  const topLevel = items.find(_ => _.slug === pageModule);
  if (!topLevel) return undefined;
  if (topLevel.children?.length) {
    const child = pageSubModule ? topLevel.children.find(_ => _.slug === pageSubModule) : undefined;
    return child ?? topLevel.children[0];
  }
  return topLevel;
};

const resolveSlugs = (items: SidebarMenuItem[], activeItem?: SidebarMenuItem) => {
  const [tabName] = (activeItem?.name ?? '').split('.');
  const topLevel = items.find(_ => _.name === tabName);
  const pageSubModule = topLevel?.children ? topLevel.children.find(_ => _.name === activeItem?.name)?.slug : undefined;
  return { pageModule: topLevel?.slug, pageSubModule };
};

export type UseSidebarMenuOptions = {
  initialPageModule?: string;
  initialPageSubModule?: string;
  onPageChange?: (pageModule?: string, pageSubModule?: string) => void;
};

export const useSidebarMenu = (groupName: string, options?: UseSidebarMenuOptions) => {
  const items = useMemo(() => {
    return SIDEBAR_GROUP_ITEMS[groupName] ?? [];
  }, [groupName]);

  const [activeItem, setActiveItem] = useState<SidebarMenuItem | undefined>(
    () => findInitialItem(items, options?.initialPageModule, options?.initialPageSubModule) ?? items[0],
  );

  const prevGroupName = useRef(groupName);

  useEffect(() => {
    if (prevGroupName.current !== groupName) {
      prevGroupName.current = groupName;
      setActiveItem(items[0]);
    }
  }, [groupName, items]);

  useEffect(() => {
    const { pageModule, pageSubModule } = resolveSlugs(items, activeItem);
    options?.onPageChange?.(pageModule, pageSubModule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, items]);

  return {
    items,
    activeItem,
    setActiveItem,
  };
};
